import _ from 'lodash';
import QueryString from 'query-string';
import axios from 'axios';
import { MULTIPART_FORM_DATA_HEADER, validateFileType } from '@utils/FileType';
import { Call, ApiUrl, HttpErrorMessage, ParseGeneralResponse } from './ApiUtils';

const addExpandOptionsToString = options => {
  let expands = '';

  if (options.scoringSurvey) expands += '&expand[]=scoring_survey';

  if (options.hasScoringSurvey) expands += '&expand[]=has_scoring_survey';

  if (options.hasSurveyResponses) expands += '&expand[]=has_survey_responses';
  if (options.hasScoringSurveyResponses) expands += '&expand[]=has_scoring_survey_responses';

  if (options.responseCount) expands += '&expand[]=response_count';
  if (options.draftResponseCount) expands += '&expand[]=draft_response_count';
  if (options.lineItemsCount) expands += '&expand[]=line_items_count';

  if (options.documents) expands += '&expand[]=vendor_documents&expand[]=bid_documents';

  // TLC: PEPPERMINT-642?
  if (options.lots) expands += '&expand[]=lots';

  return expands;
};

// TLC: PEPPERMINT-642(FETCH): 5.1 CCHs
// TLC: PEPPERMINT-642(FETCH): 5.1.1 CCHs
// TLC: PEPPERMINT-642(FETCH): 5.1.2 CCHs
// TLC: PEPPERMINT-642(FETCH): 5.1.3 CCHs
// TLC: PEPPERMINT-642(FETCH): 5.1.4 CCHs (LEGACY)
export function fetchRequest (id, additionalParams) {
  const url = ApiUrl(`/bid_requests/${id}?${additionalParams}`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          bid_request: 'bid_request',
          supplierPrequalLineItemQuestionCount: 'supplier_line_item_prequal_question_count'
        },
    },
  );
}

// Dedicated parse callback for fetchRequestChat only -- surfaces the
// 'archived'/'deleted' differentiator that `bid_request_chat` adds alongside `errors` on
// failure, without widening the shared ParseGeneralResponse (used by nearly every REST call
// in this app) for callers that don't need it.
function ParseRequestChatResponse (success, response, options, status) {
  const result = ParseGeneralResponse(success, response, options, status);

  if (!success) {
    result.reason = response?.response?.data?.reason ?? null;
  }

  return result;
}

export function getBidRequestAutomated (id) {
  const url = ApiUrl(`/bid_requests/${id}/automated`);

  return Call('get', url, {}, ParseGeneralResponse, { responseDataKeys: { automated: 'automated' } });
}

export function fetchRequestChat (id) {
  const url = ApiUrl(`/bid_request_chat/${id}`);

  return Call(
    'get',
    url,
    {},
    ParseRequestChatResponse,
    {
      responseDataKeys:
        {
          bid_request: 'bid_request_chat',
        },
    },
  );
}

export function adminOptInToChannels (id) {
  const url = ApiUrl(`/bid_requests/${id}/admin_opt_in_to_channels`);

  return Call(
    'post',
    url,
    {},
    ParseGeneralResponse,
    { responseDataKeys: { success: 'success' } },
  );
}

export function getAdminChannelOptInStatus (id) {
  const url = ApiUrl(`/bid_requests/${id}/admin_channel_opt_in_status`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    { responseDataKeys: { can_opt_out: 'can_opt_out', opted_in: 'opted_in' } },
  );
}

export function adminOptOutOfChannels (id) {
  const url = ApiUrl(`/bid_requests/${id}/admin_opt_out_of_channels`);

  return Call(
    'delete',
    url,
    {},
    ParseGeneralResponse,
    { responseDataKeys: { success: 'success' } },
  );
}

export const getRequestUsers = (id, category = '') => {
  const url = ApiUrl(`/bid_requests/${id}/users?category=${category}`);

  return Call('get', url, {}, ParseGeneralResponse, {
    responseDataKeys: { users: 'users' },
  });
};

export function saveBidRequest (data) {
  // TLC: PEPPERMINT-642(SAVE)?
  const url = ApiUrl(`/bid_requests/${data.requestId}`);
  const method = 'put';

  return Call(
    method,
    url,
    data,
    ParseGeneralResponse,
    { responseDataKeys: { bid_request: 'bid_request' }, return: { } }
  );
}

export const scheduleBidRequest = data => Call(
  'post',
  ApiUrl(`/bid_requests/${data.id}/schedule`),
  data,
  ParseGeneralResponse,
  {
    responseDataKeys: {
      bid_request: 'bid_request'
    }
  }
);

// Supplier Documents
export const saveBidDocument = async (t, binaryTypesAllowed, data) => {
  const url = ApiUrl(`/bid_requests/${data.requestId}/add_document`);
  const formData = new FormData();

  if (data.file) formData.append('file', data.file);

  formData.append('name', data.name);
  formData.append('description', data.description);
  formData.append('submission_required', data.submission_required);
  formData.append('approval_required', data.approval_required);
  formData.append('will_accept_submissions', data.will_accept_submissions);
  formData.append('blocks_access', data.blocks_access);
  formData.append('consent_required', data.consent_required);

  try {
    await validateFileType({ binaryTypesAllowed, file: data.file, t });

    const response = await axios.post(url, formData, { headers: MULTIPART_FORM_DATA_HEADER });

    return ParseGeneralResponse(true, response, {
      responseDataKeys: {
        bid_documents: 'bid_documents'
      }
    });
  } catch (error) {
    return ParseGeneralResponse(false, error, {});
  }
};

export const saveBuyerDocument = async (t, binaryTypesAllowed, data) => {
  const url = ApiUrl(`/bid_requests/${data.id}/add_buyer_document`);

  const formData = new FormData();

  formData.append('file', data.file);

  try {
    await validateFileType({ binaryTypesAllowed, file: data.file, t });

    const response = await axios.put(url, formData, { headers: MULTIPART_FORM_DATA_HEADER });

    return ParseGeneralResponse(true, response, { responseDataKeys: { buyer_documents: 'buyer_documents' } });
  } catch (error) {
    return ParseGeneralResponse(false, error, {});
  }
};

export const submitVendorDocument = async (t, binaryTypesAllowed, file, requestId, documentId) => {
  const url = ApiUrl(`/bid_responses/submit_document?id=${requestId}&document_id=${documentId}`);

  const formData = new FormData();

  formData.append('file', file);
  try {
    await validateFileType({ binaryTypesAllowed, file, t });

    const response = await axios.put(url, formData, { headers: MULTIPART_FORM_DATA_HEADER });

    const responseDataKeys = {
      document: 'document',
      missing_required_documents: 'missing_required_documents'
    };

    return ParseGeneralResponse(true, response, { responseDataKeys });
  } catch (error) {
    return ParseGeneralResponse(false, error, {});
  }
};

export const updateVendorDocument = async (t, binaryTypesAllowed, requestId, documentId, attributes) => {
  const url = ApiUrl(`/bid_requests/${requestId}/vendor_documents/${documentId}`);
  const formData = new FormData();

  formData.append('approved', attributes.approved);
  formData.append('rejection_reason', attributes.rejectionReason);

  try {
    await validateFileType({ binaryTypesAllowed, file: attributes.approved, t });

    const response = await axios.put(url, formData, { headers: MULTIPART_FORM_DATA_HEADER });

    return ParseGeneralResponse(
      true,
      response,
      { responseDataKeys: { bid_documents: 'bid_documents' } }
    );
  } catch (error) {
    return ParseGeneralResponse(false, error, {});
  }
};

export function deleteBuyerDocument (requestId, documentId) {
  const url = ApiUrl(`/bid_requests/${requestId}/delete_buyer_document/`);
  const method = 'put';

  return Call(
    method,
    url,
    { document_id: documentId },
    ParseGeneralResponse,
    {
      responseDataKeys: { documents: 'documents', errors: 'errors' },

    }
  );
}

export function deleteBidDocument (requestId, documentId) {
  const url = ApiUrl(`/bid_requests/${requestId}/delete_document/`);
  const method = 'put';

  return Call(
    method,
    url,
    { document_id: documentId },
    ParseGeneralResponse,
    {
      responseDataKeys: { documents: 'documents', errors: 'errors' },

    }
  );
}

export function changeDocumentSubmissionRequired (requestId, documentId) {
  const url = ApiUrl(`/bid_requests/${requestId}/document_submission_required/`);
  const method = 'put';

  return Call(
    method,
    url,
    { document_id: documentId },
    ParseGeneralResponse,
    {
      responseDataKeys: { documents: 'documents', errors: 'errors' },

    }
  );
}

export function cloneBidRequest (id) {
  const url = ApiUrl(`/bid_requests/${id}/clone?set_prices_from_awarded=true`);
  const method = 'post';

  return Call(
    method,
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {},

    }
  );
}

export function submitDeleteEntireEvent (id) {
  const url = ApiUrl(`/bid_requests/${id}/delete`);
  const method = 'post';

  return Call(
    method,
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: { errors: 'errors' },
    }
  );
}

export function deleteRequestLot (lotId, lotIndex) {
  const url = ApiUrl('/line_items/lot_destroy');

  return Call(
    'delete',
    url,
    {
      id: lotId,
    },
    ParseGeneralResponse,
    {
      return: { lotIndex },
    },
  );
}

// TLC: PEPPERMINT-642(DELETE)? CCHs
export function deleteAllLots (requestId) {
  const url = ApiUrl('/line_items/remove_all_lots');

  return Call(
    'delete',
    url,
    {
      bid_request_id: requestId,
    },
    ParseGeneralResponse,
    {
      responseDataKeys: {
        // TLC: PEPPERMINT-642(DELETE)? CCHs
        deleted_custom_column_headers: 'deleted_custom_column_headers',
        deleted_qual_questions: 'deleted_qual_questions',
        status: 'status'
      },
    },
  );
}

export function ParseSaveRequestUserResponse (success, response, options) {
  const result = { success };

  if (!_.isEmpty(options.return)) {
    result.return = options.return;
  }
  if (success) {
    const dataKeys = options.responseDataKeys || {};

    _.forEach(dataKeys, (value, key) => {
      const valueKey = _.isEmpty(value) ? 'data' : `data.${value}`;

      result[key] = _.get(response, valueKey, null);
    });
  } else {
    result.errors = HttpErrorMessage(response);
    result.users = _.get(response, 'response.data.users', []);
  }
  return result;
}

/**
 * @param {{
 *   page?: number,
 *   pageSize?: number,
 *   sortField?: *,
 *   sortOrder?: *,
 *   unreadEventIds?: Array<number|string>,
 * }} [params]
 * @param {AbortController|null} [controller]
 */
export function fetchBidRequestsMinimalList ({ page = 1, pageSize, sortField, sortOrder, unreadEventIds } = {}, controller = null) {
  // arrayFormat: 'bracket' serializes arrays as unread_event_ids[]=1&unread_event_ids[]=2,
  // which Rails strong_parameters parses as an array (same pattern as loadLineItems.ts).
  const queryString = QueryString.stringify(
    {
      page,
      page_size: pageSize,
      sort_field: sortField,
      sort_order: sortOrder,
      ...(unreadEventIds?.length ? { unread_event_ids: unreadEventIds } : {}),
    },
    { arrayFormat: 'bracket' }
  );
  const url = ApiUrl(`/request_list?${queryString}`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {
        bidRequests: 'bid_requests',
        totalCount: 'total_count',
      },
    },
    true,
    'json',
    controller,
  );
}

export function fetchBidRequestWithResponsesAndVendorDocuments (id, options = {}) {
  let expands = 'expand[]=lots_count&expand[]=vendors';

  expands += '&expand[]=vendor_documents&expand[]=document_responses';
  expands += '&expand[]=bid_documents&expand[]=response_count&expand[]=bid_acceptance';
  expands += addExpandOptionsToString(options);

  const additional = '&additional[]=responses';
  const url = ApiUrl(`/bid_requests/${id}?${expands}${additional}`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          bid_request: 'bid_request',
          responses: 'responses',
        },
    },
  );
}

export function fetchBidRequestWithDocuments (id, options = {}) {
  const expands = addExpandOptionsToString(options);
  const query = 'expand[]=vendor_documents&expand[]=vendors&expand[]=bid_documents&expand[]=bid_acceptance';
  const url = ApiUrl(`/bid_requests/${id}?${query}${expands}`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: { bid_request: 'bid_request' },
    }
  );
}

export function fetchResponsesWithResponseLots (id) {
  const url = ApiUrl('/bid_responses/show_responses?expand=[]response_lots');

  return Call(
    'post',
    url,
    { bid_request_id: id },
    ParseGeneralResponse,
    {
      responseDataKeys:
      {
        responses: 'responses'
      },
    },

  );
}

// TLC(FETCH): Check how data is loaded to add the Total Landed Cost.
// TLC: PEPPERMINT-642(FETCH): 5.1.5 CCHs
export function fetchVendorBid (options) {
  const { requestId, questions, vendorOrgId, bidAcceptance, lineItemPrequal } = options;

  // TLC: PEPPERMINT-642(FETCH): 5.1.5 CCHs
  let url = ApiUrl(`/bid_requests/${requestId}`);

  // Some items could be made conditional for performance when appropriate?
  // Be mindful of the question mark for beginning query string if we condition.
  url += '?expand[]=anchor_price_line_items&additional[]=anchor_pricing_disabled';
  url += '&expand[]=line_items_count&expand[]=lots_count';
  url += '&active_bid=true&additional[]=draft_bid';
  url += '&expand[]=bid_acceptance';

  if (questions) {
    url += '&additional[]=all_questions_answered&expand[]=has_line_item_prequal_survey';
    url += '&expand[]=all_survey_questions&expand[]=include_lot_dupes';
  }

  // Used when previewing lots & line items as a specific vendor org
  if (vendorOrgId) url += `&vendor_org_id=${vendorOrgId}`;
  if (bidAcceptance) url += '&additional[]=bid_acceptance';
  if (lineItemPrequal) url += '&additional[]=line_item_prequal';

  url += addExpandOptionsToString(options);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          allQuestionsAnswered: 'all_questions_answered',
          bid: 'response',
          bid_request: 'bid_request',
          bidAcceptance: 'bid_acceptance',
          draftBid: 'draft_bid',
          supplierLineItemAnswerCount: 'supplier_line_item_answer_count',
          supplierOrganizationId: 'supplier_organization_id',
          supplierPrequalLineItemAnswers: 'supplier_line_item_prequal_answers',
          supplierPrequalLineItemQuestionCount: 'supplier_line_item_prequal_question_count'
        },
    },
  );
}

export function getVendorEventGroups (requestId) {
  const url = ApiUrl(`/bid_requests/${requestId}/vendor_event_groups`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          vendor_event_groups: 'vendor_event_groups'
        },
    },
  );
}

export function fetchBidTimings (requestId) {
  const url = ApiUrl(`/bid_requests/${requestId}/bid_timings`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          all_in_line_item_round_ranks: 'all_in_line_item_round_ranks',
          all_in_lot_round_ranks: 'all_in_lot_round_ranks',
          data: 'data',
          keep_feedback_column: 'keep_feedback_column',
          line_item_round_ranks: 'line_item_round_ranks',
          lot_round_ranks: 'lot_round_ranks',
          responses: 'responses'
        },
    },
  );
}

export function fetchLowestOverallResponseLotTotals (requestId) {
  const url = ApiUrl(`/bid_requests/${requestId}/lowest_overall_response_lot_totals`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          lot_min_totals: 'lot_min_totals'
        },
    },
  );
}

export function updateUiConfig (requestId, key, value) {
  const url = ApiUrl(`/bid_requests/${requestId}/update_ui_config`);

  return Call(
    'put',
    url,
    {
      key,
      value
    },
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          ui_config: 'ui_config',
        },
    },
  );
}

export function removeUser (requestId, userIds) {
  const url = ApiUrl(`/bid_requests/${requestId}/remove_user`);
  const method = 'put';

  return Call(
    method,
    url,
    { user_ids: userIds },
    ParseGeneralResponse,
    {
      responseDataKeys: { errors: 'errors', users: 'users', vendorGroup: 'vendor_event_group' },

    }
  );
}

export function fetchVendorShortlistForRequest (id, params = {}) {
  const stringParams = QueryString.stringify(params);
  const url = ApiUrl(`/bid_requests/${id}/request_vendor_shortlist?${stringParams}`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          counts: 'counts',
          existing_supplier_emails: 'existing_supplier_emails',
          pagy: 'pagy',
          records: 'records',
          shortlisted_emails : 'shortlisted_emails'
        },
    },

  );
}

export function fetchVendorOrgsForRequest (id, orgId = null) {
  const url = ApiUrl(`/bid_requests/${id}/vendor_organizations${orgId ? `?vendor_organization_id=${orgId}` : ''}`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
      {
        overall_incumbent: 'overall_incumbent',
        vendor_organizations: 'vendor_organizations'
      }
    }
  );
}

export function fetchAllShortlistedEmailsForRequest (id, inviteStatusExceptions) {
  let url = ApiUrl(`/bid_requests/${id}/all_shortlisted_emails`);

  if (inviteStatusExceptions) url += `?invite_status_exceptions=${inviteStatusExceptions}`;

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          emails: 'emails'
        },
    },

  );
}

function parseAddVendorsToShortlistResponse (success, response, options, status) {
  const result = ParseGeneralResponse(success, response, options, status);
  if (!success) {
    result.already_invited_emails = _.get(response, 'response.data.already_invited_emails', []);
  }
  return result;
}

/**
 * Add vendors to a bid request's shortlist (`PUT /bid_requests/:id/add_vendors_to_shortlist`).
 *
 * `vendorList` is the current path and the only input that carries a Corporate Division; prefer it.
 * The backend prioritizes the collections in this order: `email_list` > `vendor_list` >
 * `vendor_profile_ids`, so populate exactly one.
 *
 * @param {string|number} id - Bid request (event) id.
 * @param {Object} data - Vendors to add. Provide exactly one collection.
 * @param {Array<{division_id?: (number|string|null), email: string, recommended?: boolean, scheduled_to_be_invited?: boolean}>} [data.vendorList]
 *   Preferred path (backend `ShortlistVendorAdder`). Each entry carries the supplier's
 *   `division_id` (persisted as `ShortlistVendorProfile.corporate_division_id`) and `recommended`
 *   (persisted as `was_recommended`). Omitting either leaves that column null (LIFECYCLE-1277).
 * @param {string[]} [data.emailList] - DEPRECATED (non-`vendorList`). Legacy add-by-email path
 *   (backend `add_emails`); carries no division, so it writes a null `corporate_division_id`.
 *   Superseded by the division-aware AddVendorsDrawer, which sends `vendorList`.
 * @param {string[]} [data.recommended] - DEPRECATED (non-`vendorList`). Emails to flag as
 *   recommended, mapped to `recommended_emails` and honored ONLY on the `emailList` path; it is
 *   ignored when `vendorList` is sent (use each `vendorList` entry's `recommended` instead).
 * @param {Array<number|string>} [data.vendorProfileIds] - DEPRECATED (non-`vendorList`). Adds by
 *   vendor profile id (backend `add_profile_ids`); carries no division. Believed unused by the UI.
 */
export function addVendorProfilesToShortlistForRequest (id, data) {
  const url = ApiUrl(`/bid_requests/${id}/add_vendors_to_shortlist`);

  return Call(
    'put',
    url,
    {
      email_list: data.emailList,
      recommended_emails: data.recommended,
      vendor_list: data.vendorList,
      vendor_profile_ids: data.vendorProfileIds
    },
    parseAddVendorsToShortlistResponse,
    {
      responseDataKeys:
        {
          emails_skipped: 'emails_skipped',
          vendor_profiles: 'vendor_profiles',
        },
    }
  );
}

export function addVendorOrganizationsToShortlistForRequest (id, data) {
  const url = ApiUrl(`/bid_requests/${id}/add_vendor_orgs_to_shortlist`);

  return Call(
    'put',
    url,
    {
      vendor_organization_ids: data.vendorOrganizationIds
    },
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          vendor_profile: 'vendor_profile',
        },
    }
  );
}

export function updateArchivedVendorReason ({ id, vendorProfileId, reason }) {
  const url = ApiUrl(`/bid_requests/${id}/bid_acceptance_archive`);
  return Call(
    'patch',
    url,
    {
      reason,
      vendor_profile_id: vendorProfileId
    },
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          // Backend returns { message, archived_selection_id, reason }
          updated: 'message',
        },
    }
  );
}

export function archiveFromShortlist ({ id, target, reason }) {
  const url = ApiUrl(`/bid_requests/${id}/bid_acceptance_archive`);

  return Call(
    'post',
    url,
    {
      reason,
      target
    },
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          // Backend returns { message, processed }
          archived: 'message',
        },
    }
  );
}

export function unarchiveFromShortlist ({ id, target }) {
  const url = ApiUrl(`/bid_requests/${id}/bid_acceptance_archive`);

  return Call(
    'delete',
    url,
    {
      target
    },
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          // Backend returns { message, processed }
          unarchived: 'message',
        },
    }
  );
}

export function deleteVendorProfilesFromShortlistForRequest (id, vendorProfileIds) {
  const url = ApiUrl(`/bid_requests/${id}/delete_vendors_from_shortlist`);

  return Call(
    'delete',
    url,
    {
      vendor_profile_ids: vendorProfileIds
    },
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          deleted: 'deleted',
        },
    }
  );
}

export function deleteVendorOrganizationsFromShortlistForRequest (id, vendorOrganizationIds) {
  const url = ApiUrl(`/bid_requests/${id}/delete_orgs_from_shortlist`);

  return Call(
    'delete',
    url,
    {
      vendor_organization_ids: vendorOrganizationIds
    },
    ParseGeneralResponse,
    {
      responseDataKeys:
        {
          deleted: 'deleted',
        },
    }
  );
}

export const inviteUsers = async (requestId, emails, options = {}, shouldAttachTable = false) => Call(
  'post',
  ApiUrl(`/bid_requests/${requestId}/invite`),
  {
    emails,
    id: requestId,
    ...options, // camelCase options won't map to Rails, so add them below
    as_collaborator: options.asCollaborator,
    file_attachment: shouldAttachTable,
    is_test: options.isTest
  },
  ParseGeneralResponse,
  {
    responseDataKeys: { sent_emails: 'sent_emails',
      unsent_emails: 'unsent_emails' }
  }
);

export const inviteAndAssociateSuppliers = async (requestId, emails, options = {}, shouldAttachTable = false) => Call(
  'post',
  ApiUrl(`/bid_requests/${requestId}/invite_and_associate_suppliers`),
  {
    emails,
    id: requestId,
    ...options,
    as_collaborator: false,
    file_attachment: shouldAttachTable,
    is_test: options.isTest
  },
  ParseGeneralResponse,
  {
    responseDataKeys: { sent_emails: 'sent_emails',
      unsent_emails: 'unsent_emails' }
  }
);

export const processStreamUserTimes = (requestId, userIdTimeMaps) => (
  Call(
    'put',
    ApiUrl(`/bid_requests/${requestId}/process_stream_engagement_measures`),
    {
      message_user_ids_times: userIdTimeMaps,
    },
    ParseGeneralResponse,
    {
      success: 'success'
    }
  )
);

export const updateOverallIncumbent = (requestId, orgId) => {
  const url = ApiUrl('/line_items/set_overall_incumbent');

  return Call(
    'put',
    url,
    {
      bid_request_id: requestId,
      vendor_organization_id: orgId
    },
    ParseGeneralResponse,
    {}
  );
};

export const updateCurrencyConfig = (bidRequestId, currencyConfigChanges = {}) => {
  const url = ApiUrl(`/events/${bidRequestId}/update_currency_config`);

  return axios.put(url, { currency_config_changes: currencyConfigChanges });
};

// EN-271: Atomically open a price-update event for submissions. The server aligns
// bid_request.state, bid_request.start_time, bidding_event.start_at, and the first
// pricing round's start_time so sync_state_from_dates can't snap us back to
// open_for_questions. Returns the freshly serialized bid_request on success.
export const openEventForSubmissions = bidRequestId => {
  const url = ApiUrl(`/events/${bidRequestId}/open_for_submissions`);

  return axios.put(url);
};

export const deleteLineItemPreQualSurvey = requestId => (
  Call(
    'delete',
    ApiUrl(`/bid_requests/${requestId}/line_item_pre_qualification_survey/`),
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {
        deleted: 'deleted',
        line_item_pre_qual_answers_submitted_count: 'line_item_pre_qual_answers_submitted_count',
        line_item_pre_qual_total_questions_count: 'line_item_pre_qual_total_questions_count'
      }
    }
  )
);

export const createSupplierSurvey = (requestId, data) => (
  Call(
    'post',
    ApiUrl(`/bid_requests/${requestId}/supplier_survey`),
    data,
    ParseGeneralResponse,
    {
      responseDataKeys: {
        survey: 'survey'
      }
    }
  )
);

/**
 * Fetches the survey for the bid reqest
 * @param {Number} requestId Id of the bid request
 * @returns {Promise | Promise<unknown>}
 */
export const fetchSupplierSurvey = requestId => (
  Call(
    'get',
    ApiUrl(`/bid_requests/${requestId}/supplier_survey`),
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {
        survey: 'survey'
      },
    }
  )
);

export function fetchResponseSummary (responseId) {
  const url = ApiUrl(`/bid_responses/${responseId}/list_summary`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
      {
        responseSummary: 'response_summary'
      },
    },

  );
}

export function fetchResponseSummaries (responseId) {
  const url = ApiUrl(`/bid_responses/${responseId}/list_summaries`);

  return Call(
    'get',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys:
      {
        responseSummaries: 'response_summaries'
      },
    },

  );
}

export const getShortlistVendorProfiles = async (params, requestId) => {
  const stringParams = QueryString.stringify(params);
  const url = ApiUrl(`/events/${requestId}/vendor_shortlist/vendor_profiles?${stringParams}`);
  return Call(
    'GET',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {
        pagy: 'pagy',
        shortlist_vendor_profile: 'shortlist_vendor_profile',
      }
    }
  );
};

export const fetchShortlistDivisions = async (params, requestId) => {
  const stringParams = QueryString.stringify(params);
  const url = ApiUrl(`/events/${requestId}/vendor_shortlist/divisions?${stringParams}`);
  return Call(
    'GET',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {
        divisions: 'divisions',
        pagy: 'pagy',
      }
    }
  );
};

export const fetchShortlistDivision = async (divisionId, requestId) => {
  const url = ApiUrl(`/events/${requestId}/vendor_shortlist/divisions/${divisionId}`);
  return Call(
    'GET',
    url,
    {},
    ParseGeneralResponse,
    {
      responseDataKeys: {
        division: 'division',
      }
    }
  );
};
